import { useEffect, useMemo, useRef, useState } from 'react';
import type { StoreProduto, StoreAdicionalOpcao } from '../../lib/api';
import type { AddToBagInput } from '../../lib/bag';
import { computeUnitPriceOrNull } from '../../lib/bag';
import {
  tamanhoInicial,
  tamanhoInicialDoSabor,
  precoSaborNoTamanho,
  pizzaUtilizavel,
  computePizzaPreco,
  MAX_ADICIONAIS_POR_PIZZA,
} from '../../lib/pizzaPreco';
import { formatBRL } from '../../lib/format';
import { categoriaEmoji } from '../../lib/catalog';
import { apiBase } from '../../lib/config';
import { rotulosAlergenos, porcaoTexto, linhasNutricional } from '../../lib/alergenos';
import { srOnly } from '../States';
import { useDialogA11y } from '../../hooks/useDialogA11y';

// Detalhe do produto (quick-view): escolha de UMA variação (radio, obrigatória se
// houver) + adicionais multi-seleção por grupo respeitando min/max/obrigatório,
// stepper de quantidade e preço calculado ao vivo. Este é o diferencial do
// cardápio sobre a loja de referência (que só tinha variante única).

interface Props {
  produto: StoreProduto;
  slug: string;
  onClose: () => void;
  onAdd: (input: AddToBagInput) => void;
  /**
   * Dono 08/09 — card de SABOR na grade de Pizzas abre esta MESMA montagem já com o sabor marcado (pizza
   * inteira, 1 parte); o cliente segue livre para adicionar a outra metade/tamanho/borda. `null`/ausente =
   * comportamento anterior (montagem vazia — usado pelo card "Monte sua pizza" e por produtos sem pizza).
   * Defensivo: um id que não exista em `pizza.sabores` é ignorado (abre vazia).
   */
  saborInicialId?: string | null;
}

function limiteLabel(min: number, max: number, obrigatorio: boolean): string {
  if (max > 0 && min > 0 && max === min) return `Escolha ${min}`;
  if (max > 0 && min > 0) return `Escolha de ${min} a ${max}`;
  if (max > 0) return `Até ${max}`;
  if (min > 0) return `Escolha ao menos ${min}`;
  return obrigatorio ? 'Obrigatório' : 'Opcional';
}

/**
 * QA-28 M-03 — a POLÍTICA de preço de vários sabores nunca era explicada: com 2 sabores de R$ 50 e R$ 60 o
 * botão dizia R$ 60 (MAIOR_SABOR) ou R$ 55 (média) sem dizer por quê — parecia erro de conta. Texto por
 * política (espelha PrecoPizzaService/lib/pizzaPreco.ts).
 */
function textoPolitica(politica: NonNullable<StoreProduto['pizza']>['politicaPreco']): string {
  switch (politica) {
    case 'MAIOR_SABOR':
      return 'Mais de um sabor: cobramos o valor do sabor mais caro.';
    case 'MEDIA_PONDERADA':
      return 'Mais de um sabor: cobramos a média dos sabores escolhidos.';
    case 'BASE_MAIS_ACRESCIMOS':
      return 'Mais de um sabor: preço base do tamanho + a média dos sabores escolhidos.';
  }
}

export function QuickView({ produto, slug, onClose, onAdd, saborInicialId = null }: Props) {
  const temVariacoes = produto.variacoes.length > 0;
  const pizza = produto.pizza ?? null;
  // A6 — card de SABOR (StoreView expande cada sabor em 1 card próprio) abre esta MESMA montagem via
  // `saborInicialId`; o cabeçalho deve então mostrar o NOME/DESCRIÇÃO do sabor clicado (não "Pizza"/produto.nome
  // genérico) — é o que o cliente reconhece de ter clicado. Resolvido uma vez, contra o `saborInicialId` (prop
  // fixa desta montagem) — não muda se o cliente depois desmarcar esse sabor. "Monte sua pizza" (sem
  // `saborInicialId`) mantém o cabeçalho do produto, como antes.
  const saborInicial = saborInicialId && pizza ? pizza.sabores.find((s) => s.id === saborInicialId) ?? null : null;
  // Fase 7.1 — defensivo (produtos de mocks/versões antigas podem não trazer os campos novos).
  const alergContem = produto.alergenosContem ?? [];
  const alergPode = produto.alergenosPodeConter ?? [];
  // Fase 7.2 — CARROSSEL: foto principal (se houver) + fotos da galeria, todas servidas por URL pública.
  const fotos = useMemo(() => {
    const arr: string[] = [];
    if (produto.produtoId && produto.temImagem) {
      arr.push(
        `${apiBase()}/api/v1/public/${encodeURIComponent(slug)}/produtos/${produto.produtoId}/imagem?v=${produto.imagemVersao ?? 0}`,
      );
    }
    for (const gid of produto.galeriaImagemIds ?? []) {
      arr.push(
        `${apiBase()}/api/v1/public/${encodeURIComponent(slug)}/produtos/${produto.produtoId}/galeria/${gid}`,
      );
    }
    return arr;
  }, [produto.produtoId, produto.temImagem, produto.imagemVersao, produto.galeriaImagemIds, slug]);
  const [fotoIdx, setFotoIdx] = useState(0);
  const fotoAtual = fotos.length ? fotos[Math.min(fotoIdx, fotos.length - 1)] : null;
  const [variacaoId, setVariacaoId] = useState<string | null>(
    temVariacoes ? produto.variacoes[0].id : null
  );
  // Seleção de adicionais por grupo (ids das opções).
  const [selecao, setSelecao] = useState<Record<string, string[]>>({});
  // C3 — tamanho inicial = MESMO helper compartilhado que o card usa pro preço (menor `ordem`), não
  // `tamanhos[0]` bruto: senão card e modal podiam divergir quando o array não vem ordenado por `ordem`.
  // QA-28 C-01 — com um sabor PRÉ-MARCADO (card de sabor), o tamanho inicial é resolvido EM FUNÇÃO DESSE
  // SABOR (`tamanhoInicialDoSabor`: 1º tamanho por `ordem` em que ele tem preço). Antes abria no tamanho
  // inicial genérico: se o sabor não tinha preço ali, o checkbox nascia marcado E desabilitado, `canAdd`
  // nunca habilitava e o cliente não tinha como sair — venda perdida em silêncio.
  const [pizzaTamanhoId, setPizzaTamanhoId] = useState<string | null>(() => {
    if (!pizza) return null;
    const doSabor = saborInicialId ? tamanhoInicialDoSabor(pizza, saborInicialId) : null;
    return doSabor?.id ?? tamanhoInicial(pizza)?.id ?? null;
  });
  // Sabor pré-marcado (card de sabor na grade): só entra se existir de fato no config desta pizza —
  // defensivo contra um id obsoleto/estranho (a montagem cai para vazia, igual ao "Monte sua pizza").
  const [pizzaSaborIds, setPizzaSaborIds] = useState<string[]>(() =>
    saborInicialId && pizza?.sabores.some((s) => s.id === saborInicialId) ? [saborInicialId] : []
  );
  // QA-28 M-02 — aviso do que a UI mudou SOZINHA na seleção (sabor substituído ao bater o máximo, sabor
  // retirado por não existir no novo tamanho). Antes o item simplesmente desaparecia da lista, sem palavra.
  const [avisoSabor, setAvisoSabor] = useState<string | null>(null);
  const [pizzaBordaId, setPizzaBordaId] = useState<string | null>(null);
  // Adicionais DENTRO da pizza (V388), na ordem em que o cliente marcou. Até MAX_ADICIONAIS_POR_PIZZA.
  const [pizzaAdicionalIds, setPizzaAdicionalIds] = useState<string[]>([]);
  const [qty, setQty] = useState(1);
  // Observação por item (QA MÉDIO): o backend já suporta (PedidoCardapioItem.observacao); faltava a UI.
  const OBS_MAX = 200;
  const [observacao, setObservacao] = useState('');
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // QA-28 M-07 — Tab preso ao diálogo + fundo `aria-hidden` + foco de volta no card que abriu.
  useDialogA11y(dialogRef);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Esc fecha.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const variacao = useMemo(
    () => produto.variacoes.find((v) => v.id === variacaoId) ?? null,
    [produto.variacoes, variacaoId]
  );

  const opcoesSelecionadas = useMemo<StoreAdicionalOpcao[]>(() => {
    const out: StoreAdicionalOpcao[] = [];
    for (const g of produto.adicionalGrupos) {
      const ids = selecao[g.id] ?? [];
      // Carrega o grupo e a POLÍTICA dele em cada escolha: num grupo `MAIOR` ("meia a meia") só a opção
      // mais cara entra no preço, e sem isso a sacola somaria — o cliente veria um total e o servidor
      // cobraria outro.
      for (const o of g.opcoes) {
        if (ids.includes(o.id)) {
          out.push({ ...o, grupoId: g.id, politicaGrupo: g.politicaPreco ?? 'SOMA' });
        }
      }
    }
    return out;
  }, [produto.adicionalGrupos, selecao]);

  const montagemPizza = useMemo(() => pizza && pizzaTamanhoId ? {
    tamanhoId: pizzaTamanhoId,
    sabores: pizzaSaborIds.map((saborId) => ({ saborId, partes: 1 })),
    ...(pizzaBordaId ? { bordaId: pizzaBordaId } : {}),
    ...(pizzaAdicionalIds.length ? { adicionalIds: pizzaAdicionalIds } : {}),
  } : undefined, [pizza, pizzaTamanhoId, pizzaSaborIds, pizzaBordaId, pizzaAdicionalIds]);
  const tamanhoPizza = pizza?.tamanhos.find((t) => t.id === pizzaTamanhoId) ?? (pizza ? tamanhoInicial(pizza) ?? undefined : undefined);
  const saboresPizza = pizza?.sabores.filter((s) => pizzaSaborIds.includes(s.id)) ?? [];
  // F2-B7 — EXIBIÇÃO do botão "Adicionar": SEM o fallback 0 de `computeUnitPrice` (nunca "R$ 0,00" enquanto a
  // montagem está incompleta — canAdd já bloqueia o envio; isto é cosmético). `null` = ainda não precificável.
  const unitPriceDisplay = computeUnitPriceOrNull(produto, variacao, opcoesSelecionadas, montagemPizza);
  // F2-M2 — pizza sem tamanho/sabor UTILIZÁVEL (o lojista inativou/zerou tudo, mas o produto continua ativo):
  // em vez de renderizar os fieldsets de tamanho/sabor com ZERO opções (beco sem saída, botão travado sem
  // explicação), mostra uma explicação clara e pula a montagem inteira.
  const pizzaIndisponivel = !!pizza && !pizzaUtilizavel(pizza);

  // Regras: cada grupo obrigatório/min precisa do mínimo; se há variações, uma escolhida.
  // QA lote4 — grupo OPCIONAL (obrigatorio=false) é válido com 0 escolhas; o min só é cobrado QUANDO o
  // cliente escolhe algo (espelha o PrecoItemService/backend e o PDV/ProductSheet). Antes, min>0 num grupo
  // opcional deixava o "Adicionar" travado e forçava marcar/pagar um adicional dispensável.
  const gruposOk = produto.adicionalGrupos.every((g) => {
    const n = (selecao[g.id] ?? []).length;
    const maxOk = g.max <= 0 || n <= g.max;
    if (g.obrigatorio) return n >= Math.max(1, g.min) && maxOk;
    return n === 0 || (n >= g.min && maxOk);
  });
  // Esgotado (estoque): trava o "Adicionar" no detalhe também (o card já desabilita, mas o modal pode
  // ser aberto para ver a foto/descrição). Espelha a regra do backend/cardápio.
  // A2 — um sabor sem preço no tamanho escolhido é INDISPONÍVEL: o checkbox fica desabilitado (ver render
  // abaixo) e, defensivamente aqui também, a montagem não pode ser adicionada se algum selecionado ficou
  // sem preço (ex.: tamanho trocado por outro caminho que não `trocarTamanho`).
  const pizzaOk = !pizza || (!!tamanhoPizza && saboresPizza.length >= 1
    && saboresPizza.length <= tamanhoPizza.maxSabores
    && saboresPizza.every((s) => precoSaborNoTamanho(pizza, s.id, tamanhoPizza.id) != null)
    && (pizza.permiteMisturaDoceSalgada || new Set(saboresPizza.map((s) => s.tipo)).size <= 1));
  const canAdd = !produto.esgotado && pizzaOk && (!!pizza || ((!temVariacoes || !!variacao) && gruposOk));
  // QA-28 C-01 — sabores SELECIONADOS que não têm preço no tamanho atual. Único caso em que a montagem fica
  // travada sem culpa do cliente (ele veio de um card de sabor); precisa de uma saída ESCRITA, não de um
  // "Escolha as opções obrigatórias" genérico.
  const saboresSemPreco =
    pizza && tamanhoPizza
      ? saboresPizza.filter((s) => precoSaborNoTamanho(pizza, s.id, tamanhoPizza.id) == null)
      : [];

  function toggleOpcao(groupId: string, opcaoId: string, max: number) {
    setSelecao((prev) => {
      const atual = prev[groupId] ?? [];
      const tem = atual.includes(opcaoId);
      if (tem) return { ...prev, [groupId]: atual.filter((x) => x !== opcaoId) };
      if (max === 1) return { ...prev, [groupId]: [opcaoId] }; // grupo de escolha única
      if (max > 0 && atual.length >= max) return prev; // grupo cheio
      return { ...prev, [groupId]: [...atual, opcaoId] };
    });
  }

  function handleAdd() {
    if (!canAdd) return;
    const borda = pizza?.bordas.find((b) => b.id === pizzaBordaId);
    const adicionaisPizza = pizzaAdicionalIds
      .map((id) => pizza?.adicionais?.find((a) => a.id === id))
      .filter((a): a is NonNullable<typeof a> => a != null);
    onAdd({
      produto,
      variacao: pizza ? null : variacao,
      adicionais: pizza ? [] : opcoesSelecionadas,
      qty,
      observacao,
      ...(montagemPizza ? { montagemPizza } : {}),
      ...(pizza ? { pizzaResumo: [
        tamanhoPizza?.nome ?? '',
        ...saboresPizza.map((s) => (saboresPizza.length > 1 ? `1/${saboresPizza.length} ${s.nome}` : s.nome)),
        ...(borda ? [`Borda ${borda.nome}`] : []),
        ...adicionaisPizza.map((a) => `+ ${a.nome}`),
      ].filter(Boolean) } : {}),
    });
  }

  /** Marca/desmarca um adicional da pizza; com o limite atingido, marcar outro não faz nada (o checkbox já trava). */
  function togglePizzaAdicional(id: string) {
    setPizzaAdicionalIds((atual) => {
      if (atual.includes(id)) return atual.filter((x) => x !== id);
      return atual.length >= MAX_ADICIONAIS_POR_PIZZA ? atual : [...atual, id];
    });
  }

  /** Nome do sabor por id (para as mensagens de aviso — M-02). */
  function nomeSabor(id: string): string {
    return pizza?.sabores.find((s) => s.id === id)?.nome ?? 'Sabor';
  }

  function togglePizzaSabor(id: string) {
    if (!pizza || !tamanhoPizza) return;
    setPizzaSaborIds((atual) => {
      if (atual.includes(id)) {
        setAvisoSabor(null);
        return atual.filter((x) => x !== id);
      }
      // A2 — defesa em profundidade: mesmo que o onChange dispare por algum outro caminho, um sabor sem
      // preço no tamanho atual nunca entra na seleção (o checkbox já vem `disabled` no render).
      if (precoSaborNoTamanho(pizza, id, tamanhoPizza.id) == null) return atual;
      const candidato = pizza.sabores.find((s) => s.id === id);
      const primeiro = pizza.sabores.find((s) => s.id === atual[0]);
      if (!pizza.permiteMisturaDoceSalgada && primeiro && candidato && primeiro.tipo !== candidato.tipo) return atual;
      if (atual.length < tamanhoPizza.maxSabores) {
        setAvisoSabor(null);
        return [...atual, id];
      }
      // QA-28 M-02 — no máximo de sabores, marcar outro DESCARTA o primeiro. Isso continua (é o
      // comportamento esperado de "trocar"), mas agora é ANUNCIADO: antes um sabor já escolhido
      // simplesmente apagava da lista e nada explicava.
      setAvisoSabor(
        `Máximo de ${tamanhoPizza.maxSabores} ${tamanhoPizza.maxSabores === 1 ? 'sabor' : 'sabores'} neste tamanho - trocamos ${nomeSabor(atual[0])} por ${nomeSabor(id)}.`
      );
      return [...atual.slice(1), id];
    });
  }

  /** M5 — trocar de tamanho NÃO zera a seleção silenciosamente: mantém os sabores que continuam com preço
   *  no novo tamanho, cortando ao novo `maxSabores` (mantém os primeiros escolhidos). Um sabor que só existia
   *  no tamanho anterior (A2 — sem preço no novo) é removido, igual a quando o cliente o desmarcaria manualmente. */
  function trocarTamanho(novoTamanho: NonNullable<typeof pizza>['tamanhos'][number]) {
    if (!pizza) return;
    setPizzaTamanhoId(novoTamanho.id);
    setPizzaSaborIds((atual) => {
      const semPrecoNoNovo = atual.filter((id) => precoSaborNoTamanho(pizza, id, novoTamanho.id) == null);
      const mantidos = atual.filter((id) => precoSaborNoTamanho(pizza, id, novoTamanho.id) != null);
      const cortados = mantidos.slice(novoTamanho.maxSabores);
      // QA-28 M-02 — troca de tamanho podia REMOVER sabores em silêncio (sem preço no novo tamanho, ou
      // acima do novo maxSabores). Agora o cliente lê exatamente o que saiu e por quê.
      const motivos: string[] = [];
      if (semPrecoNoNovo.length > 0) {
        motivos.push(
          `${semPrecoNoNovo.map(nomeSabor).join(', ')} não ${semPrecoNoNovo.length === 1 ? 'existe' : 'existem'} no tamanho ${novoTamanho.nome} e foi retirado da montagem.`
        );
      }
      if (cortados.length > 0) {
        motivos.push(
          `${novoTamanho.nome} aceita até ${novoTamanho.maxSabores} ${novoTamanho.maxSabores === 1 ? 'sabor' : 'sabores'} - retiramos ${cortados.map(nomeSabor).join(', ')}.`
        );
      }
      setAvisoSabor(motivos.length > 0 ? motivos.join(' ') : null);
      return mantidos.slice(0, novoTamanho.maxSabores);
    });
  }

  /** QA-28 M-03 — preço da montagem ATUAL em um tamanho candidato (para o cliente comparar Broto × Grande
   *  sem selecionar cada um). Com sabores já escolhidos e todos com preço no tamanho, é o valor EXATO
   *  (mesma aritmética do servidor); senão, o menor preço de sabor daquele tamanho como "a partir de". */
  function precoDoTamanho(t: NonNullable<typeof pizza>['tamanhos'][number]): { valor: number; exato: boolean } | null {
    if (!pizza) return null;
    const selecionados = pizzaSaborIds.filter((id) => precoSaborNoTamanho(pizza, id, t.id) != null);
    if (selecionados.length > 0 && selecionados.length === pizzaSaborIds.length) {
      const exato = computePizzaPreco(
        pizza,
        t.id,
        selecionados.slice(0, t.maxSabores).map((saborId) => ({ saborId, partes: 1 })),
        pizzaBordaId,
        pizzaAdicionalIds,
      );
      if (exato != null) return { valor: exato, exato: true };
    }
    const precos = pizza.sabores
      .map((s) => precoSaborNoTamanho(pizza, s.id, t.id))
      .filter((p): p is number => p != null);
    if (precos.length === 0) return null;
    return { valor: Math.min(...precos), exato: false };
  }

  const emoji = categoriaEmoji(produto.categoria || produto.nome);

  return (
    <div
      className="loja-overlay loja-overlay--center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        className="loja-modal"
        role="dialog"
        aria-modal="true"
        aria-label={saborInicial ? saborInicial.nome : produto.nome}
      >
        <button
          ref={closeRef}
          className="loja-close loja-close--float"
          onClick={onClose}
          aria-label="Fechar detalhes"
        >
          ×
        </button>

        <div className="loja-modal-media">
          <span className="loja-modal-emoji" aria-hidden="true">
            {emoji}
          </span>
          {/* Fase 7.2 — carrossel: foto principal + galeria. Sem fotos → só o emoji (QA L5: gate por temImagem/id). */}
          {fotoAtual && (
            <img
              key={fotoAtual}
              className="loja-modal-photo"
              src={fotoAtual}
              alt=""
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          {fotos.length > 1 && (
            <>
              <button
                type="button"
                className="loja-carousel-nav loja-carousel-prev"
                aria-label="Foto anterior"
                onClick={() => setFotoIdx((i) => (i - 1 + fotos.length) % fotos.length)}
              >
                ‹
              </button>
              <button
                type="button"
                className="loja-carousel-nav loja-carousel-next"
                aria-label="Próxima foto"
                onClick={() => setFotoIdx((i) => (i + 1) % fotos.length)}
              >
                ›
              </button>
              <div className="loja-carousel-dots" aria-hidden="true">
                {fotos.map((f, i) => (
                  <span key={f} className={'loja-carousel-dot' + (i === Math.min(fotoIdx, fotos.length - 1) ? ' is-on' : '')} />
                ))}
              </div>
              {/* QA-28 B-05 — a posição no carrossel era APENAS a bolinha colorida (dots com aria-hidden):
                  quem usa leitor de tela (ou não distingue a cor) não sabia em que foto estava. */}
              <span style={srOnly} aria-live="polite">
                Foto {Math.min(fotoIdx, fotos.length - 1) + 1} de {fotos.length}
              </span>
            </>
          )}
        </div>

        <div className="loja-modal-body">
          <span className="loja-card-cat">{produto.categoria || 'Cardápio'}</span>
          <h2 className="loja-modal-name">{saborInicial ? saborInicial.nome : produto.nome}</h2>
          {(saborInicial ? saborInicial.descricao : produto.descricao) && (
            <p className="loja-modal-desc">{saborInicial ? saborInicial.descricao : produto.descricao}</p>
          )}
          {/* Pendência 15 (23/10/2026): a DESCRIÇÃO LONGA do produto, abaixo do resumo, com as quebras de linha do
              lojista. O sabor da pizza tem só a descrição dele. */}
          {!saborInicial && produto.descricaoLonga && (
            <p className="loja-modal-desc-longa">{produto.descricaoLonga}</p>
          )}

          {/* Fase 7.1 — dieta (selo mais forte), alérgenos e tabela nutricional. */}
          {(produto.vegano || produto.vegetariano) && (
            <div className="loja-modal-diet">
              <span className={'loja-diet ' + (produto.vegano ? 'loja-diet--vegano' : 'loja-diet--veg')}>
                {produto.vegano ? 'Vegano' : 'Vegetariano'}
              </span>
            </div>
          )}
          {(alergContem.length > 0 || alergPode.length > 0) && (
            <div className="loja-modal-alerg" role="note">
              {alergContem.length > 0 && (
                <p className="loja-alerg-line loja-alerg-contem">
                  <b>⚠ Contém:</b> {rotulosAlergenos(alergContem).join(', ')}
                </p>
              )}
              {alergPode.length > 0 && (
                <p className="loja-alerg-line loja-alerg-pode">
                  <b>Pode conter:</b> {rotulosAlergenos(alergPode).join(', ')}
                </p>
              )}
            </div>
          )}
          {produto.nutricional && (
            <details className="loja-nutri">
              <summary>
                Informação nutricional
                {porcaoTexto(produto.nutricional) && (
                  <span className="loja-nutri-porcao"> · porção {porcaoTexto(produto.nutricional)}</span>
                )}
              </summary>
              <table className="loja-nutri-table">
                <tbody>
                  {linhasNutricional(produto.nutricional).map((l) => (
                    <tr key={l.rotulo}>
                      <th scope="row">{l.rotulo}</th>
                      <td>
                        {l.valor.toLocaleString('pt-BR')} {l.unidade}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {pizza && pizzaIndisponivel && (
            // F2-M2 — nunca renderiza "1. Escolha o tamanho"/"2. Escolha os sabores" SEM nenhuma opção: isso
            // era um beco sem saída (fieldset mudo, botão travado para sempre, sem explicar o motivo).
            <p className="loja-modal-note" role="alert">
              Esta pizza está indisponível no momento. Fale com a loja ou escolha outro item do cardápio.
            </p>
          )}

          {pizza && !pizzaIndisponivel && <>
            <fieldset className="loja-group">
              <legend className="loja-group-label">1. Escolha o tamanho <span className="loja-group-req">Obrigatório</span></legend>
              <div className="loja-group-opts">{pizza.tamanhos.map((t) => {
                // QA-28 M-03 — cada tamanho passa a mostrar VALOR: com a seleção atual precificável, o preço
                // exato daquele tamanho; senão, "a partir de" o menor sabor dele. Antes, comparar Broto ×
                // Grande exigia selecionar cada um e reler o botão.
                const p = precoDoTamanho(t);
                return <label key={t.id} className={`loja-opt${tamanhoPizza?.id === t.id ? ' is-on' : ''}`}>
                  <input type="radio" name="pizza-tamanho" checked={tamanhoPizza?.id === t.id} onChange={() => trocarTamanho(t)} />
                  <span className="loja-opt-name">{t.nome}<small className="loja-opt-porcao">{t.fatias} fatias · até {t.maxSabores} {t.maxSabores === 1 ? 'sabor' : 'sabores'}</small></span>
                  {p != null && (
                    <span className="loja-opt-price">
                      {p.exato ? formatBRL(p.valor) : <>a partir de {formatBRL(p.valor)}</>}
                    </span>
                  )}
                </label>;
              })}</div>
            </fieldset>
            <fieldset className="loja-group">
              <legend className="loja-group-label">2. Escolha os sabores <span className="loja-group-req">{pizzaSaborIds.length}/{tamanhoPizza?.maxSabores ?? 1}</span></legend>
              {/* QA-28 M-03 — a regra de preço de vários sabores agora está ESCRITA (maior sabor / média /
                  base + média). Sem isso, o total parecia erro de conta. Só quando o tamanho aceita >1. */}
              {(tamanhoPizza?.maxSabores ?? 1) > 1 && (
                <p className="loja-modal-note" id="pizza-politica-hint">
                  {textoPolitica(pizza.politicaPreco)}
                </p>
              )}
              {/* M6 — hint visível do porquê alguns sabores ficam desabilitados (mistura doce/salgada), em vez
                  de um checkbox mudo travado. Só aparece quando há de fato um bloqueio em potencial (mistura
                  proibida e já existe ao menos 1 sabor escolhido). */}
              {!pizza.permiteMisturaDoceSalgada && pizzaSaborIds.length > 0 && (
                <p className="loja-modal-note" id="pizza-mix-hint">
                  Esta pizza não mistura sabores doces e salgados.
                </p>
              )}
              {/* QA-28 M-02 — o que a montagem mudou sozinha (sabor trocado no máximo, sabor retirado ao
                  trocar de tamanho). `role="status"` para o leitor de tela anunciar sem roubar o foco. */}
              {avisoSabor && (
                <p className="loja-modal-note loja-modal-note--aviso" role="status">
                  <span aria-hidden="true">ℹ</span> {avisoSabor}
                </p>
              )}
              {/* QA-28 C-01 — saída ESCRITA quando um sabor selecionado (ex.: o do card clicado) não tem
                  preço no tamanho aberto: antes o checkbox vinha marcado e desabilitado, "Adicionar" ficava
                  travado para sempre e a única nota era "Escolha as opções obrigatórias para continuar". */}
              {saboresSemPreco.length > 0 && tamanhoPizza && (
                <p className="loja-modal-note loja-modal-note--alerta" role="alert">
                  <span aria-hidden="true">⚠</span>{' '}
                  {saboresSemPreco.map((s) => s.nome).join(', ')}{' '}
                  {saboresSemPreco.length === 1 ? 'não está disponível' : 'não estão disponíveis'} no tamanho{' '}
                  {tamanhoPizza.nome}. Escolha outro tamanho ou remova o sabor para continuar.
                </p>
              )}
              <div className="loja-group-opts">{pizza.sabores.map((s) => {
                const on = pizzaSaborIds.includes(s.id);
                const primeiro = pizza.sabores.find((x) => x.id === pizzaSaborIds[0]);
                const bloqueadoPorMistura = !on && !pizza.permiteMisturaDoceSalgada && !!primeiro && primeiro.tipo !== s.tipo;
                // A2 — preço ausente no tamanho = sabor INDISPONÍVEL ali (nunca `?? 0`: custaria "de graça" e o
                // servidor recusaria o pedido). Desabilita o checkbox e mostra o motivo no lugar do preço.
                const preco = tamanhoPizza ? precoSaborNoTamanho(pizza, s.id, tamanhoPizza.id) : undefined;
                const semPreco = preco == null;
                // QA-28 C-01 — um sabor JÁ MARCADO sempre pode ser DESmarcado: `semPreco` só desabilita
                // quando ele não está selecionado. Antes o checkbox saía `checked` + `disabled` — o input
                // desabilitado não dispara onChange, então não havia como remover o sabor nem como descobrir
                // que a saída era trocar o tamanho.
                const disabled = (!on && semPreco) || bloqueadoPorMistura;
                return <label key={s.id} className={`loja-opt${on ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={disabled}
                    aria-describedby={bloqueadoPorMistura ? 'pizza-mix-hint' : undefined}
                    onChange={() => togglePizzaSabor(s.id)}
                  />
                  <span className="loja-opt-name">{s.nome}<small className="loja-opt-porcao">{s.tipo === 'DOCE' ? 'Doce' : 'Salgada'}</small></span>
                  {semPreco ? (
                    <span className="loja-opt-price">Indisponível neste tamanho</span>
                  ) : (
                    <span className="loja-opt-price">{formatBRL(preco)}</span>
                  )}
                </label>;
              })}</div>
              {saboresPizza.length > 1 && <p className="loja-modal-note">{saboresPizza.map((s) => `1/${saboresPizza.length} ${s.nome}`).join(' · ')}</p>}
            </fieldset>
            {pizza.bordas.length > 0 && <fieldset className="loja-group">
              <legend className="loja-group-label">3. Escolha a borda <span className="loja-group-req is-opt">Opcional</span></legend>
              <div className="loja-group-opts">
                <label className={`loja-opt${!pizzaBordaId ? ' is-on' : ''}`}><input type="radio" name="pizza-borda" checked={!pizzaBordaId} onChange={() => setPizzaBordaId(null)} /><span className="loja-opt-name">Sem borda</span></label>
                {pizza.bordas.map((b) => <label key={b.id} className={`loja-opt${pizzaBordaId === b.id ? ' is-on' : ''}`}><input type="radio" name="pizza-borda" checked={pizzaBordaId === b.id} onChange={() => setPizzaBordaId(b.id)} /><span className="loja-opt-name">{b.nome}</span><span className="loja-opt-price">+ {formatBRL(b.preco)}</span></label>)}
              </div>
            </fieldset>}
            {/* Adicionais DENTRO da pizza (V388, dono 06/10/2026): escolha múltipla, até 7, somados ao preço. */}
            {!!pizza.adicionais?.length && <fieldset className="loja-group">
              <legend className="loja-group-label">{pizza.bordas.length > 0 ? 4 : 3}. Adicionais <span className="loja-group-req is-opt">Opcional · {pizzaAdicionalIds.length}/{MAX_ADICIONAIS_POR_PIZZA}</span></legend>
              <div className="loja-group-opts">{pizza.adicionais.map((a) => {
                const on = pizzaAdicionalIds.includes(a.id);
                const disabled = !on && pizzaAdicionalIds.length >= MAX_ADICIONAIS_POR_PIZZA;
                return <label key={a.id} className={`loja-opt${on ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}>
                  <input type="checkbox" checked={on} disabled={disabled} onChange={() => togglePizzaAdicional(a.id)} />
                  <span className="loja-opt-name">{a.nome}</span>
                  {a.preco > 0 && <span className="loja-opt-price">+ {formatBRL(a.preco)}</span>}
                </label>;
              })}</div>
            </fieldset>}
            {/* QA-28 M-12 — adicionais cadastrados num produto de PIZZA não eram renderizados e o payload ia
                com `adicionais: []`: o cliente nunca via o "bacon extra" e o lojista não sabia que cadastrou
                em vão. O backend RECUSA os dois juntos (RegistrarPedidoCardapioService: "Item de pizza montada
                não aceita variação nem adicionais avulsos."), então enviá-los daria 400 — a correção possível
                aqui é DIZER, em vez de omitir em silêncio. */}
            {produto.adicionalGrupos.length > 0 && (
              <p className="loja-modal-note loja-modal-note--aviso" role="note">
                <span aria-hidden="true">ℹ</span> Adicionais avulsos não entram na pizza montada - escolha a
                borda acima ou combine com a loja no atendimento.
              </p>
            )}
          </>}

          {!pizza && temVariacoes && (
            <fieldset className="loja-group">
              <legend className="loja-group-label">
                Escolha uma opção <span className="loja-group-req">Obrigatório</span>
              </legend>
              <div className="loja-group-opts">
                {produto.variacoes.map((v) => (
                  <label
                    key={v.id}
                    className={`loja-opt${variacaoId === v.id ? ' is-on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="variacao"
                      checked={variacaoId === v.id}
                      onChange={() => setVariacaoId(v.id)}
                    />
                    <span className="loja-opt-name">
                      {v.nome}
                      {(v.porcaoDescricao || v.rendimentoPessoas) && (
                        <small className="loja-opt-porcao">
                          {[v.porcaoDescricao, v.rendimentoPessoas ? `serve ${v.rendimentoPessoas}` : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </small>
                      )}
                    </span>
                    <span className="loja-opt-price">{formatBRL(v.preco)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {!pizza && produto.adicionalGrupos.map((g) => {
            const sel = selecao[g.id] ?? [];
            const cheio = g.max > 0 && sel.length >= g.max;
            return (
              <fieldset className="loja-group" key={g.id}>
                <legend className="loja-group-label">
                  {g.nome}
                  <span className={`loja-group-req${g.obrigatorio ? '' : ' is-opt'}`}>
                    {limiteLabel(g.min, g.max, g.obrigatorio)}
                  </span>
                </legend>
                <div className="loja-group-opts">
                  {g.opcoes.map((o) => {
                    const on = sel.includes(o.id);
                    const disabled = !on && cheio && g.max !== 1;
                    return (
                      <label
                        key={o.id}
                        className={`loja-opt${on ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={disabled}
                          onChange={() => toggleOpcao(g.id, o.id, g.max)}
                        />
                        <span className="loja-opt-name">{o.nome}</span>
                        {o.preco > 0 && (
                          <span className="loja-opt-price">+ {formatBRL(o.preco)}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}

          <div className="loja-group loja-obs">
            <label className="loja-group-label" htmlFor="quickview-obs">
              Observação <span className="loja-group-req is-opt">Opcional</span>
            </label>
            <textarea
              id="quickview-obs"
              className="loja-obs-input"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value.slice(0, OBS_MAX))}
              maxLength={OBS_MAX}
              rows={2}
              placeholder="Ex.: sem cebola, ponto da carne…"
            />
          </div>

          <div className="loja-modal-buy">
            <div className="loja-qty loja-qty--lg" role="group" aria-label="Quantidade">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Diminuir quantidade">
                −
              </button>
              <span aria-live="polite">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(99, q + 1))} aria-label="Aumentar quantidade">
                +
              </button>
            </div>
            <button
              className="loja-btn loja-btn--primary loja-btn--lg loja-btn--grow"
              disabled={!canAdd}
              onClick={handleAdd}
            >
              {produto.esgotado
                ? 'Esgotado'
                : pizzaIndisponivel
                  ? 'Indisponível'
                  // F2-B7 — nunca "Adicionar R$ 0,00" enquanto a montagem está incompleta (0 sabores, sabor
                  // sem preço no tamanho): `unitPriceDisplay` é `null` nesses casos (ver lib/bag.ts).
                  : unitPriceDisplay != null
                    ? `Adicionar ${formatBRL(unitPriceDisplay * qty)}`
                    : 'Adicionar'}
            </button>
          </div>
          {produto.esgotado ? (
            <p className="loja-modal-note">Produto esgotado no momento.</p>
          ) : pizzaIndisponivel ? null /* já explicado acima, junto do fieldset omitido */ : (
            // QA-28 C-01 — não repete a nota genérica quando a causa REAL já está escrita acima (sabor sem
            // preço no tamanho): duas mensagens, uma delas inútil, empurravam o cliente para o beco.
            !canAdd && saboresSemPreco.length === 0 && (
              <p className="loja-modal-note">Escolha as opções obrigatórias para continuar.</p>
            )
          )}
        </div>
      </div>
    </div>
  );
}
